import { createFileRoute, Link } from "@tanstack/react-router";
import { Leaf, PawPrint, Repeat, TrendingUp } from "lucide-react";
import { PageHead, Panel, StatCard } from "@/components/dashboard-shell";
import { formatINR, stories, getInstitution } from "@/data/sample";
import { campaigns, programs, getPartner, domainName } from "@/data/network";

export const Route = createFileRoute("/donor/impact")({ component: ImpactTracker });

const supported = ["fodder-for-cows", "surgery-fund"];

function ImpactTracker() {
  const myCampaigns = campaigns.slice(0, 4);
  const mySubs = programs.slice(0, 2);
  const monthly = mySubs.reduce((s, p) => s + p.monthly, 0);

  return (
    <>
      <PageHead
        title="Impact tracker"
        subtitle="Follow every rupee you gave — campaign progress, recurring giving and field updates."
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Campaigns supported" value={String(myCampaigns.length)} tone="primary" />
        <StatCard label="Monthly recurring" value={formatINR(monthly)} tone="good" />
        <StatCard label="Animals & people helped" value="612" tone="warn" />
        <StatCard label="Partner orgs followed" value="5" />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Panel title="Campaign progress you funded">
          <ul className="space-y-4 text-sm">
            {myCampaigns.map((c) => {
              const pct = Math.min(100, Math.round((c.raised / c.goal) * 100));
              const org = getPartner(c.orgId);
              return (
                <li key={c.id}>
                  <div className="flex items-center justify-between gap-3">
                    <Link
                      to="/campaigns/$id"
                      params={{ id: c.id }}
                      className="font-medium hover:underline"
                    >
                      {c.title}
                    </Link>
                    <span className="text-xs font-semibold text-[var(--maroon)]">{pct}%</span>
                  </div>
                  <p className="text-xs text-foreground/70">
                    {org?.name} · {domainName(c.domain)}
                  </p>
                  <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-[var(--cream-2)]">
                    <div
                      className="h-full"
                      style={{ width: `${pct}%`, background: "var(--gradient-warm)" }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </Panel>

        <Panel title="Your recurring giving">
          <ul className="divide-y divide-border text-sm">
            {mySubs.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 py-3">
                <div>
                  <p className="flex items-center gap-1.5 font-medium">
                    <Repeat className="h-3.5 w-3.5 text-[var(--saffron-deep)]" />
                    {p.title}
                  </p>
                  <p className="text-xs text-foreground/70">
                    {p.impact} · {getPartner(p.orgId)?.name}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-[var(--maroon)]">{formatINR(p.monthly)}/mo</p>
                  <button className="mt-1 rounded-full border border-border px-2.5 py-0.5 text-[11px]">
                    Pause
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <Link
            to="/donor/discover"
            className="mt-4 inline-block text-xs font-semibold text-[var(--maroon)] underline"
          >
            Add a monthly programme →
          </Link>
        </Panel>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Panel title="Field updates from your causes">
          <ul className="space-y-4">
            {stories.slice(0, 4).map((s) => (
              <li key={s.id} className="flex gap-3">
                <img
                  src={s.image}
                  alt=""
                  loading="lazy"
                  className="h-14 w-14 rounded-lg object-cover"
                />
                <div>
                  <Link
                    to="/stories/$id"
                    params={{ id: s.id }}
                    className="text-sm font-medium leading-snug hover:underline"
                  >
                    {s.title}
                  </Link>
                  <p className="text-xs text-foreground/70">
                    {getInstitution(s.institutionId)?.name} · {s.date}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </Panel>
        <Panel title="Impact summary">
          <ul className="space-y-3 text-sm text-foreground/75">
            <li className="flex items-center gap-2">
              <PawPrint className="h-4 w-4 text-[var(--maroon)]" /> 84 cattle fed for a full month
            </li>
            <li className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-[var(--success)]" /> 6 emergency surgeries funded
            </li>
            <li className="flex items-center gap-2">
              <Leaf className="h-4 w-4 text-[var(--success)]" /> 120 saplings planted in your name
            </li>
          </ul>
          <p className="mt-4 text-xs text-foreground/70">
            Every claim above is backed by expense proofs uploaded by partner organisations before
            their payout is released.
          </p>
        </Panel>
      </div>
    </>
  );
}

void supported;
